import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ContentType, ThemeMode } from '@/types';

const THEME_KEY = 'relatario:theme';
const TYPE_KEY = 'relatario:contentType';

interface UiState {
  theme: ThemeMode;
  contentType: ContentType;
  /**
   * Tipo impuesto por la historia que se está mirando. Gana sobre
   * `contentType` mientras dure la navegación, sin pisar la preferencia
   * guardada: es la decisión de producto sobre entrar por link directo.
   */
  overrideType: ContentType | null;
  setTheme: (theme: ThemeMode) => void;
  setContentType: (type: ContentType) => void;
  setOverrideType: (type: ContentType | null) => void;
}

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Escribe el tema en el <html>, que es de donde salen los tokens. */
export function applyTheme(theme: ThemeMode): void {
  const dark = theme === 'dark' || (theme === 'system' && prefersDark());
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';

  /*
   * La barra del navegador acompaña al tema elegido, no al del sistema.
   * Los <meta theme-color> del index.html usan `prefers-color-scheme`, que
   * ignora el interruptor de la app: alguien con el sistema en claro y la app
   * en oscuro veía la barra clara sobre una página oscura.
   */
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  if (meta) meta.content = dark ? '#16130F' : '#FBF7F0';
}

/** Escribe el acento activo en el <html>. */
export function applyContentType(type: ContentType): void {
  document.documentElement.dataset.contentType = type;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      contentType: 'fanfic',
      overrideType: null,

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      setContentType: (contentType) => {
        applyContentType(contentType);
        set({ contentType, overrideType: null });
      },

      setOverrideType: (overrideType) => {
        applyContentType(overrideType ?? get().contentType);
        set({ overrideType });
      },
    }),
    {
      name: 'relatario:ui',
      // Guardamos también las claves sueltas que lee el script inline del
      // index.html, para que el primer pintado ya salga con el tema correcto.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        applyTheme(state.theme);
        applyContentType(state.contentType);
      },
    },
  ),
);

/** El tipo efectivo: el de la historia abierta, o la preferencia guardada. */
export function useEffectiveContentType(): ContentType {
  return useUiStore((s) => s.overrideType ?? s.contentType);
}

/** Mantiene sincronizadas las claves planas que lee el script del index. */
useUiStore.subscribe((state) => {
  try {
    localStorage.setItem(THEME_KEY, state.theme);
    localStorage.setItem(TYPE_KEY, state.contentType);
  } catch {
    // Modo privado sin storage: el tema simplemente no persiste.
  }
});

/** Reaplica el tema cuando cambia la preferencia del sistema. */
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useUiStore.getState().theme === 'system') applyTheme('system');
  });
}
